import { Component } from "react";

/**
 * ErrorBoundary — catches any uncaught render/effect exception in the app tree
 * and renders an editorial, non-scary fallback with a "Reload" CTA.
 *
 * Wraps the whole app (App.js). Individual pages don't need their own boundary.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      message: (error && (error.message || String(error))) || "Something went wrong.",
    };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info?.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="error-boundary-fallback"
        className="min-h-screen flex flex-col items-center justify-center bg-white px-6 text-center"
      >
        <div className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mb-4">
          Error — 500
        </div>
        <h1 className="font-display font-black text-5xl md:text-6xl tracking-[-0.03em] leading-tight mb-4">
          Something broke on our end.
        </h1>
        <p className="text-zinc-500 max-w-md mb-8 leading-relaxed">
          The page failed to render. This has been logged. Reload the page —
          if it happens again, please let us know at{" "}
          <a href="mailto:hello@conferencely.org" className="underline">
            hello@conferencely.org
          </a>
          .
        </p>
        <details className="text-xs font-mono text-zinc-400 max-w-md mb-8 border border-zinc-200 p-3 open:bg-[#FAFAFA] cursor-pointer">
          <summary className="cursor-pointer">Technical details</summary>
          <div className="mt-2 text-start break-words whitespace-pre-wrap">
            {this.state.message}
          </div>
        </details>
        <button
          onClick={this.handleReload}
          data-testid="error-boundary-reload"
          className="btn-primary"
        >
          Reload the page
        </button>
      </div>
    );
  }
}
